import React, { useState, useRef, useEffect, ReactNode } from "react";
import classNames from 'classnames';
import {getElementContentHeight} from './VerticalDropdownHeightComputing';
import './VerticalDropdown.scss';

interface VerticalDropdownProps {
  children: ReactNode,
  customButton?: ReactNode,
  maxHeight?: number,
  containerClassName?: string,
}

export default function VerticalDropdown(props: VerticalDropdownProps) {
  const initHeight = () => props.maxHeight ? `${props.maxHeight}px` : "0px";
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(initHeight());
  const [isContentBiggerThanMaxHeight, setIsContentBiggerThanMaxHeight] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  function handleResize() {
    if (isOpen) {
      const contentHeight = getElementContentHeight(contentRef.current)
      setHeight(contentHeight + "px");
    } else {
      setHeight(initHeight());
    }
  }

  // Resize handling
  useEffect(() => {
    handleResize();
    setIsContentBiggerThanMaxHeight(contentRef.current.scrollHeight > props.maxHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // cleanup method
  }, [isOpen]);

  // Auto close the dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (contentRef.current && !contentRef.current.parentElement?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside); // cleanup method
  }, []);

  return (
    <div
      className={classNames(
        {["active"]: isOpen },
        'vertical-dropdown-container',
        props.containerClassName)}
      onClick={() => setIsOpen((previous) => !previous)}
    >
      {props.customButton}
      <div
        ref={contentRef}
        className={classNames(
          {["active"]: isOpen },
          {["content-only"]: !props.customButton },
          {["hide-content"]: isContentBiggerThanMaxHeight},
          'vertical-dropdown-content',
          'clickable')}
        style={{ maxHeight: height }}
      >
        {props.children}
      </div>
    </div>
  );
}
